import { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, X, Loader2, Sparkles, ArrowLeftRight } from "lucide-react";
import { usePortfolioImages, useAddPortfolioImage, useDeletePortfolioImage } from "@/hooks/usePortfolio";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const MAX_PHOTOS = 5;

// ── Utilitaires Canvas ───────────────────────────────────────────────────────

function loadImg(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Impossible de charger l'image")); };
    img.src = url;
  });
}

/** Dessine une image en mode "object-fit: cover" dans un rectangle donné */
function drawCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  dx: number, dy: number, dw: number, dh: number
) {
  const ratio = Math.max(dw / img.width, dh / img.height);
  const sw = dw / ratio;
  const sh = dh / ratio;
  const sx = (img.width - sw) / 2;
  const sy = (img.height - sh) / 2;
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
}

/** Badge "AVANT" / "APRÈS" semi-transparent */
function drawBadge(ctx: CanvasRenderingContext2D, text: string, x: number, y: number) {
  const PAD_X = 16;
  const H = 38;
  const R = 8;
  ctx.font = 'bold 20px "Space Grotesk", system-ui, sans-serif';
  const tw = ctx.measureText(text).width;
  const bw = tw + PAD_X * 2;

  ctx.shadowColor = "rgba(0,0,0,0.25)";
  ctx.shadowBlur = 6;
  ctx.fillStyle = "rgba(0,0,0,0.52)";
  ctx.beginPath();
  ctx.roundRect(x, y, bw, H, R);
  ctx.fill();

  ctx.shadowBlur = 0;
  ctx.fillStyle = "#ffffff";
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  ctx.fillText(text, x + PAD_X, y + H / 2);
}

/** Génère le collage Avant/Après et retourne un File JPEG */
async function buildCollage(
  beforeFile: File,
  afterFile: File,
  title: string
): Promise<File> {
  const W = 1200;
  const IMG_H = 700;
  const TITLE_H = title ? 68 : 0;
  const HALF = W / 2;

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = IMG_H + TITLE_H;
  const ctx = canvas.getContext("2d")!;

  const [before, after] = await Promise.all([loadImg(beforeFile), loadImg(afterFile)]);

  // Photo gauche (avant)
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, HALF, IMG_H);
  ctx.clip();
  drawCover(ctx, before, 0, 0, HALF, IMG_H);
  ctx.restore();

  // Vignette haut-gauche pour lisibilité du badge
  const vigL = ctx.createLinearGradient(0, 0, 0, 90);
  vigL.addColorStop(0, "rgba(0,0,0,0.38)");
  vigL.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = vigL;
  ctx.fillRect(0, 0, HALF, 90);

  // Photo droite (après)
  ctx.save();
  ctx.beginPath();
  ctx.rect(HALF, 0, HALF, IMG_H);
  ctx.clip();
  drawCover(ctx, after, HALF, 0, HALF, IMG_H);
  ctx.restore();

  const vigR = ctx.createLinearGradient(0, 0, 0, 90);
  vigR.addColorStop(0, "rgba(0,0,0,0.38)");
  vigR.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = vigR;
  ctx.fillRect(HALF, 0, HALF, 90);

  // Ligne de séparation
  ctx.fillStyle = "rgba(255,255,255,0.72)";
  ctx.fillRect(HALF - 1, 0, 2, IMG_H);

  // Badges
  drawBadge(ctx, "AVANT", 16, 16);
  drawBadge(ctx, "APRÈS", HALF + 16, 16);

  // Barre de titre
  if (title) {
    const grad = ctx.createLinearGradient(0, IMG_H, 0, IMG_H + TITLE_H);
    grad.addColorStop(0, "#1a0d06");
    grad.addColorStop(1, "#231005");
    ctx.fillStyle = grad;
    ctx.fillRect(0, IMG_H, W, TITLE_H);

    ctx.font = 'bold 24px "Space Grotesk", system-ui, sans-serif';
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.shadowBlur = 0;
    ctx.fillText(title, W / 2, IMG_H + TITLE_H / 2, W - 80);
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("Échec de la génération du collage"));
        resolve(new File([blob], "avant-apres.jpg", { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.92
    );
  });
}

// ── Composant ────────────────────────────────────────────────────────────────

type UploadMode = "single" | "collage";

const PortfolioUpload = () => {
  const { user } = useAuth();
  const { data: images = [], isLoading } = usePortfolioImages(user?.id);
  const addImage = useAddPortfolioImage();
  const deleteImage = useDeletePortfolioImage();

  // Mode d'upload
  const [mode, setMode] = useState<UploadMode>("single");

  // Photo simple
  const singleRef = useRef<HTMLInputElement>(null);
  const [caption, setCaption] = useState("");

  // Collage avant/après
  const beforeRef = useRef<HTMLInputElement>(null);
  const afterRef = useRef<HTMLInputElement>(null);
  const [beforeFile, setBeforeFile] = useState<File | null>(null);
  const [afterFile, setAfterFile] = useState<File | null>(null);
  const [beforePreview, setBeforePreview] = useState<string | null>(null);
  const [afterPreview, setAfterPreview] = useState<string | null>(null);
  const [collageTitle, setCollageTitle] = useState("");
  const [collageLoading, setCollageLoading] = useState(false);

  const count = images.length;
  const isFull = count >= MAX_PHOTOS;
  const remaining = MAX_PHOTOS - count;

  // Nettoyage des URLs de prévisualisation à la destruction
  useEffect(() => {
    return () => {
      if (beforePreview) URL.revokeObjectURL(beforePreview);
      if (afterPreview) URL.revokeObjectURL(afterPreview);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetCollage = () => {
    if (beforePreview) URL.revokeObjectURL(beforePreview);
    if (afterPreview) URL.revokeObjectURL(afterPreview);
    setBeforeFile(null);
    setAfterFile(null);
    setBeforePreview(null);
    setAfterPreview(null);
    setCollageTitle("");
  };

  // ── Handlers ──

  const handleSingleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isFull) {
      toast({ title: "Portfolio complet", description: `Limite de ${MAX_PHOTOS} photos atteinte.`, variant: "destructive" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Fichier trop volumineux", description: "Maximum 5 Mo par photo", variant: "destructive" });
      return;
    }
    try {
      await addImage.mutateAsync({ file, caption: caption.trim() || undefined });
      setCaption("");
      toast({ title: "Photo ajoutée", description: `${count + 1} photo${count + 1 > 1 ? "s" : ""} dans votre portfolio` });
    } catch (err: any) {
      toast({ title: "Erreur", description: err.message, variant: "destructive" });
    }
    if (singleRef.current) singleRef.current.value = "";
  };

  const handleBeforeSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (beforePreview) URL.revokeObjectURL(beforePreview);
    setBeforeFile(file);
    setBeforePreview(URL.createObjectURL(file));
    if (beforeRef.current) beforeRef.current.value = "";
  };

  const handleAfterSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (afterPreview) URL.revokeObjectURL(afterPreview);
    setAfterFile(file);
    setAfterPreview(URL.createObjectURL(file));
    if (afterRef.current) afterRef.current.value = "";
  };

  const handleCreateCollage = async () => {
    if (!beforeFile || !afterFile) return;
    if (isFull) {
      toast({ title: "Portfolio complet", description: `Limite de ${MAX_PHOTOS} photos atteinte.`, variant: "destructive" });
      return;
    }
    setCollageLoading(true);
    try {
      const merged = await buildCollage(beforeFile, afterFile, collageTitle.trim());
      await addImage.mutateAsync({ file: merged, caption: collageTitle.trim() || "Avant / Après" });
      resetCollage();
      setMode("single");
      toast({ title: "Collage créé !", description: `${count + 1} photo${count + 1 > 1 ? "s" : ""} dans votre portfolio` });
    } catch (err: any) {
      toast({ title: "Erreur lors de la fusion", description: err.message, variant: "destructive" });
    }
    setCollageLoading(false);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteImage.mutateAsync(id);
      toast({ title: "Photo supprimée" });
    } catch (err: any) {
      toast({ title: "Erreur", description: err.message, variant: "destructive" });
    }
  };

  return (
    <Card className="shadow-warm-md overflow-hidden">
      <CardContent className="p-0">

        {/* ── En-tête + jauge ── */}
        <div className="px-7 pt-7 pb-6 border-b border-border/50">
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-primary mb-2">Portfolio</p>
          <h2 className="font-display text-xl font-bold leading-snug">Montrez votre savoir-faire</h2>
          <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
            Vos photos parlent avant vous — ajoutez jusqu'à{" "}
            <span className="font-semibold text-foreground">{MAX_PHOTOS} réalisations</span>{" "}
            pour convaincre vos futurs clients.
          </p>

          <div className="mt-5 space-y-2">
            <div
              className="flex gap-1.5"
              role="progressbar"
              aria-valuenow={count}
              aria-valuemin={0}
              aria-valuemax={MAX_PHOTOS}
              aria-label={`${count} photos sur ${MAX_PHOTOS}`}
            >
              {Array.from({ length: MAX_PHOTOS }).map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    "h-1.5 flex-1 rounded-full transition-all duration-500",
                    i < count ? "bg-primary" : "bg-muted"
                  )}
                />
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {count === 0 && "Aucune photo ajoutée — commencez dès maintenant"}
              {count > 0 && !isFull && (
                <><span className="font-semibold text-foreground">{count}</span> photo{count > 1 ? "s" : ""} ajoutée{count > 1 ? "s" : ""}{" · encore "}
                  <span className="font-semibold text-foreground">{remaining}</span> emplacement{remaining > 1 ? "s" : ""} libre{remaining > 1 ? "s" : ""}</>
              )}
              {isFull && <span className="font-semibold text-primary">Portfolio complet ✓</span>}
            </p>
          </div>
        </div>

        {/* ── Zone d'ajout ── */}
        <div className="px-7 py-6 space-y-4">
          {isFull ? (
            <div className="rounded-2xl bg-accent/[0.07] border border-accent/20 px-6 py-5 text-center space-y-1.5">
              <Sparkles className="mx-auto h-5 w-5 text-accent" aria-hidden="true" />
              <p className="font-display font-semibold text-foreground">Portfolio complet !</p>
              <p className="text-sm text-muted-foreground">Supprimez une photo ci-dessous pour la remplacer.</p>
            </div>
          ) : (
            <>
              {/* Sélecteur de mode */}
              <div className="flex gap-1 p-1 rounded-xl bg-muted/60 w-fit">
                <button
                  type="button"
                  onClick={() => { setMode("single"); resetCollage(); }}
                  className={cn(
                    "flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150",
                    mode === "single" ? "bg-card shadow-warm-sm text-foreground" : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Camera className="h-3.5 w-3.5" aria-hidden="true" />
                  Photo
                </button>
                <button
                  type="button"
                  onClick={() => setMode("collage")}
                  className={cn(
                    "flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150",
                    mode === "collage" ? "bg-card shadow-warm-sm text-foreground" : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <ArrowLeftRight className="h-3.5 w-3.5" aria-hidden="true" />
                  Avant / Après
                </button>
              </div>

              {/* ── Photo simple ── */}
              {mode === "single" && (
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => singleRef.current?.click()}
                    disabled={addImage.isPending}
                    className={cn(
                      "w-full rounded-2xl border-2 border-dashed py-9 px-6 text-center cursor-pointer",
                      "border-primary/25 bg-primary/[0.02] transition-all duration-200",
                      "hover:border-primary/50 hover:bg-primary/[0.05] hover:shadow-warm-sm",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
                      "disabled:opacity-50 disabled:cursor-wait"
                    )}
                  >
                    {addImage.isPending ? (
                      <div className="flex flex-col items-center gap-3">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">Envoi en cours…</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-3">
                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 mx-auto">
                          <Camera className="h-7 w-7 text-primary" aria-hidden="true" />
                        </div>
                        <div>
                          <p className="font-display font-semibold text-foreground">Choisir une photo</p>
                          <p className="text-xs text-muted-foreground mt-0.5">JPG, PNG, WEBP · 5 Mo max</p>
                        </div>
                      </div>
                    )}
                  </button>
                  <Input
                    placeholder="Légende (ex : Réfection salle de bain, Alger 2024)"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    className="h-10 bg-background text-sm"
                    disabled={addImage.isPending}
                  />
                  <input ref={singleRef} type="file" accept="image/*" className="hidden" onChange={handleSingleFile} />
                </div>
              )}

              {/* ── Collage Avant / Après ── */}
              {mode === "collage" && (
                <div className="space-y-4">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Racontez votre transformation — choisissez une photo{" "}
                    <span className="font-semibold text-foreground">avant</span> et une{" "}
                    <span className="font-semibold text-foreground">après</span> votre intervention.
                    Le collage est assemblé en un clic et compte pour{" "}
                    <span className="font-semibold text-foreground">1 réalisation</span> sur {MAX_PHOTOS}.
                  </p>

                  {/* Zones Avant / Après */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* AVANT */}
                    <div className="space-y-1.5">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Avant</p>
                      <button
                        type="button"
                        onClick={() => beforeRef.current?.click()}
                        disabled={collageLoading}
                        className={cn(
                          "relative w-full aspect-square rounded-xl overflow-hidden border-2 border-dashed transition-all duration-200",
                          beforeFile ? "border-primary/40" : "border-border/60 hover:border-primary/40 hover:bg-primary/[0.03]",
                          "disabled:opacity-50 disabled:cursor-wait"
                        )}
                      >
                        {beforePreview ? (
                          <>
                            <img src={beforePreview} alt="Avant" className="h-full w-full object-cover" />
                            <div className="absolute inset-0 bg-black/0 hover:bg-black/15 transition-colors" />
                            <span className="absolute top-2 left-2 bg-black/55 text-white text-[10px] font-bold px-2 py-0.5 rounded-md backdrop-blur-sm">
                              AVANT
                            </span>
                          </>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full gap-2 p-3">
                            <Camera className="h-6 w-6 text-muted-foreground/40" aria-hidden="true" />
                            <p className="text-[11px] text-muted-foreground text-center leading-snug">Choisir la photo avant</p>
                          </div>
                        )}
                      </button>
                      <input ref={beforeRef} type="file" accept="image/*" className="hidden" onChange={handleBeforeSelect} />
                    </div>

                    {/* APRÈS */}
                    <div className="space-y-1.5">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Après</p>
                      <button
                        type="button"
                        onClick={() => afterRef.current?.click()}
                        disabled={collageLoading}
                        className={cn(
                          "relative w-full aspect-square rounded-xl overflow-hidden border-2 border-dashed transition-all duration-200",
                          afterFile ? "border-primary/40" : "border-border/60 hover:border-primary/40 hover:bg-primary/[0.03]",
                          "disabled:opacity-50 disabled:cursor-wait"
                        )}
                      >
                        {afterPreview ? (
                          <>
                            <img src={afterPreview} alt="Après" className="h-full w-full object-cover" />
                            <div className="absolute inset-0 bg-black/0 hover:bg-black/15 transition-colors" />
                            <span className="absolute top-2 left-2 bg-black/55 text-white text-[10px] font-bold px-2 py-0.5 rounded-md backdrop-blur-sm">
                              APRÈS
                            </span>
                          </>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full gap-2 p-3">
                            <Camera className="h-6 w-6 text-muted-foreground/40" aria-hidden="true" />
                            <p className="text-[11px] text-muted-foreground text-center leading-snug">Choisir la photo après</p>
                          </div>
                        )}
                      </button>
                      <input ref={afterRef} type="file" accept="image/*" className="hidden" onChange={handleAfterSelect} />
                    </div>
                  </div>

                  <Input
                    placeholder="Titre de la réalisation (ex : Réfection salle de bain, Alger)"
                    value={collageTitle}
                    onChange={(e) => setCollageTitle(e.target.value)}
                    className="h-10 bg-background text-sm"
                    disabled={collageLoading}
                  />

                  <Button
                    type="button"
                    variant="hero"
                    className="w-full gap-2"
                    disabled={!beforeFile || !afterFile || collageLoading}
                    onClick={handleCreateCollage}
                  >
                    {collageLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Génération du collage…
                      </>
                    ) : (
                      <>
                        <ArrowLeftRight className="h-4 w-4" aria-hidden="true" />
                        Créer le collage Avant / Après
                      </>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Galerie ── */}
        {isLoading ? (
          <div className="px-7 pb-7 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="aspect-square rounded-2xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : images.length > 0 ? (
          <div className="px-7 pb-7 space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border/50" />
              <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Vos réalisations</p>
              <div className="h-px flex-1 bg-border/50" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {images.map((img, idx) => (
                <div
                  key={img.id}
                  className="group relative aspect-square overflow-hidden rounded-2xl border border-border/50 bg-muted shadow-warm-sm"
                >
                  <img
                    src={img.image_url}
                    alt={img.caption || `Réalisation ${idx + 1}`}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  <button
                    onClick={() => handleDelete(img.id)}
                    className={cn(
                      "absolute top-2 right-2 flex h-7 w-7 items-center justify-center",
                      "rounded-full bg-black/50 text-white backdrop-blur-sm",
                      "opacity-0 group-hover:opacity-100 transition-all duration-200",
                      "hover:bg-destructive hover:scale-110"
                    )}
                    aria-label="Supprimer cette photo"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                  {img.caption && (
                    <p className="absolute bottom-2 left-2 right-8 text-xs text-white font-medium leading-snug line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      {img.caption}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : null}

      </CardContent>
    </Card>
  );
};

export default PortfolioUpload;
